import { PageHeader } from "@bednbody/ui/components/page-header";
import { Empty } from "@bednbody/ui/components/empty";
import { NativeSelectOption } from "@bednbody/ui/components/native-select";
import { NativeSelect } from "@bednbody/ui/components/native-select";
import { Badge } from "@bednbody/ui/components/badge";
import { Callout } from "@bednbody/ui/components/callout";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import {
  BuildingsIcon,
  CalendarCheckIcon,
  CaretRightIcon,
  MapPinIcon,
  PlusIcon,
  UsersIcon,
  WalletIcon,
} from "@phosphor-icons/react";
import { lazy, Suspense, useState } from "react";
import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Input } from "@bednbody/ui/components/input";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  parseHubPoint,
  useHubsAdmin,
  useHubPortfolioSummary,
  useCreateHub,
  type HubPortfolioSummary,
} from "./hubs.hooks";

const HubLocationPicker = lazy(() =>
  import("./HubLocationPicker").then((module) => ({ default: module.HubLocationPicker })),
);

const REGIONS = ["NCR", "Rizal"] as const;
const pesos = new Intl.NumberFormat("en-PH", {
  style: "currency",
  currency: "PHP",
  maximumFractionDigits: 0,
});

const EMPTY_SUMMARY: HubPortfolioSummary = {
  bookings: 0,
  cancelledBookings: 0,
  deliveredValue: 0,
  activeStaff: 0,
};

function HubCard({
  hub,
  summary,
}: {
  hub: {
    id: string;
    name: string;
    region: string;
    geo: unknown;
    is_pilot: boolean | null;
    bookable: boolean | null;
    service_radius_km: number;
  };
  summary: HubPortfolioSummary;
}) {
  const location = parseHubPoint(hub.geo);

  return (
    <Link
      className="group flex min-h-64 flex-col rounded-2xl border border-border bg-card p-5 shadow-xs transition-all hover:-translate-y-0.5 hover:border-primary/45 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      to={`/hubs/${hub.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold text-foreground">{hub.name}</h2>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPinIcon size={14} />
            {hub.region} · {hub.service_radius_km} km coverage
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {/* Booking state first: it is the one that decides whether clients
              reach this hub. "Pilot" is a label and says nothing about it. */}
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
              hub.bookable ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"
            }`}
          >
            {hub.bookable ? "Open" : "Closed"}
          </span>
          {hub.is_pilot ? (
            <Badge variant="primary-soft" shape="soft">
              Pilot
            </Badge>
          ) : null}
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-4 border-y border-border py-4">
        <div>
          <dt className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <CalendarCheckIcon size={14} /> Bookings
          </dt>
          <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
            {summary.bookings}
          </dd>
        </div>
        <div>
          <dt className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <WalletIcon size={14} /> Delivered value
          </dt>
          <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
            {pesos.format(summary.deliveredValue)}
          </dd>
        </div>
        <div>
          <dt className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <UsersIcon size={14} /> Active staff
          </dt>
          <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
            {summary.activeStaff}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Cancelled</dt>
          <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
            {summary.cancelledBookings}
          </dd>
        </div>
      </dl>

      <div className="mt-auto flex items-center justify-between gap-3 pt-4 text-xs">
        <span className={location ? "text-success" : "text-muted-foreground"}>
          {location ? "Map location configured" : "Map location not configured"}
        </span>
        <span className="flex items-center gap-1 font-semibold text-primary">
          Open hub{" "}
          <CaretRightIcon size={14} className="transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}

function CreateHubDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createHub = useCreateHub();
  const [name, setName] = useState("");
  const [region, setRegion] = useState<string>("NCR");
  const [radius, setRadius] = useState("12.5");
  const [clockInRadiusM, setClockInRadiusM] = useState("");
  const [isPilot, setIsPilot] = useState(false);
  // Defaults false, matching the column default (0056): a hub is invisible to
  // client booking until deliberately opened.
  const [bookable, setBookable] = useState(false);
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");

  const radiusNum = Number(radius);
  const clockInRadiusNum = Number(clockInRadiusM);
  const latNum = lat.trim() === "" ? null : Number(lat);
  const lngNum = lng.trim() === "" ? null : Number(lng);
  // 0330: no longer held to 10–15 km. The engines cap every hub at the Settings
  // service area, which a Super Admin may now move, so the only check left is
  // a real positive distance (the database's own rule).
  const validRadius = Number.isFinite(radiusNum) && radiusNum > 0 && radiusNum <= 999.9;
  // Same shape for this one: an operational input the hub creator owns, not a
  // guessed default, so the only check is "a real positive number".
  const validClockInRadiusM = Number.isFinite(clockInRadiusNum) && clockInRadiusNum > 0;
  const validLat = latNum === null || (Number.isFinite(latNum) && latNum >= -90 && latNum <= 90);
  const validLng = lngNum === null || (Number.isFinite(lngNum) && lngNum >= -180 && lngNum <= 180);
  const coordsPaired = (latNum === null) === (lngNum === null);
  const canSubmit =
    name.trim().length > 0 &&
    validRadius &&
    validClockInRadiusM &&
    validLat &&
    validLng &&
    coordsPaired &&
    !createHub.isPending;

  function reset() {
    setName("");
    setRegion("NCR");
    setRadius("12.5");
    setClockInRadiusM("");
    setIsPilot(false);
    setBookable(false);
    setLat("");
    setLng("");
    createHub.reset();
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    try {
      const created = await createHub.mutateAsync({
        name: name.trim(),
        region,
        serviceRadiusKm: radiusNum,
        clockInRadiusM: clockInRadiusNum,
        isPilot,
        bookable,
        lat: latNum,
        lng: lngNum,
      });
      toast.success(`${created.name} is ready`, {
        description: "Its hub workspace is now available in the portfolio.",
      });
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create hub.");
    }
  }

  return (
    <Dialog
      onOpenChange={(nextOpen) => {
        if (!nextOpen) reset();
        onOpenChange(nextOpen);
      }}
      open={open}
    >
      <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <div className="mb-1 flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <BuildingsIcon size={20} weight="fill" />
          </div>
          <DialogTitle>Create a hub</DialogTitle>
          <DialogDescription>
            Set the operating area first. You can assign people from Roster after the hub is
            created.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Hub identity</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                How this location appears across operations.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-[1.3fr_0.7fr]">
              <label className="grid gap-1.5 text-sm font-medium text-foreground">
                Hub name
                <Input
                  autoFocus
                  onChange={(event) => setName(event.target.value)}
                  placeholder="e.g. Cainta Central"
                  required
                  value={name}
                />
              </label>
              <label className="grid gap-1.5 text-sm font-medium text-foreground">
                Region
                <NativeSelect
                  className="h-9 w-full"
                  onChange={(event) => setRegion(event.target.value)}
                  value={region}
                >
                  {REGIONS.map((item) => (
                    <NativeSelectOption key={item} value={item}>
                      {item}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </label>
            </div>
          </section>

          <section className="space-y-3 border-t border-border pt-5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Coverage and map</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Search an address, then refine its exact pin on the map.
                </p>
              </div>
              <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground">
                Capped by the service area in Settings
              </span>
            </div>
            <div className="grid gap-4 sm:grid-cols-[0.55fr_1fr]">
              <label className="grid gap-1.5 text-sm font-medium text-foreground">
                Service radius
                <div className="relative">
                  <Input
                    min={0.1}
                    onChange={(event) => setRadius(event.target.value)}
                    required
                    step="0.1"
                    type="number"
                    value={radius}
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
                    km
                  </span>
                </div>
              </label>
              <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs leading-5 text-muted-foreground">
                Search the address below to pin the hub automatically. You can fine-tune the pin
                before creating it.
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-[0.55fr_1fr]">
              <label className="grid gap-1.5 text-sm font-medium text-foreground">
                Clock-in radius
                <div className="relative">
                  <Input
                    min={1}
                    onChange={(event) => setClockInRadiusM(event.target.value)}
                    placeholder="e.g. 150"
                    required
                    step="1"
                    type="number"
                    value={clockInRadiusM}
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
                    m
                  </span>
                </div>
              </label>
              <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs leading-5 text-muted-foreground">
                How close a rider's GPS must be to this hub to clock in. No hub-wide default —
                riders at this hub can't clock in until this is set.
              </div>
            </div>
            <Suspense fallback={<Skeleton className="h-64 motion-reduce:animate-none" />}>
              <HubLocationPicker
                latitude={validLat ? latNum : null}
                longitude={validLng ? lngNum : null}
                onChange={(point) => {
                  setLat(point.lat.toFixed(6));
                  setLng(point.lng.toFixed(6));
                }}
              />
            </Suspense>
            <details className="rounded-xl border border-border px-3 py-2.5">
              <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                Enter coordinates manually
              </summary>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1.5 text-sm font-medium text-foreground">
                  Latitude
                  <Input
                    onChange={(event) => setLat(event.target.value)}
                    placeholder="14.5786"
                    step="any"
                    type="number"
                    value={lat}
                  />
                </label>
                <label className="grid gap-1.5 text-sm font-medium text-foreground">
                  Longitude
                  <Input
                    onChange={(event) => setLng(event.target.value)}
                    placeholder="121.1222"
                    step="any"
                    type="number"
                    value={lng}
                  />
                </label>
              </div>
            </details>
            {!coordsPaired ? (
              <Callout tone="warning">Enter both latitude and longitude to enable the map.</Callout>
            ) : (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <MapPinIcon size={14} />
                {latNum === null
                  ? "No map location will be saved yet."
                  : "Map location will be saved with this hub."}
              </p>
            )}
          </section>

          {/* The consequential one. `bookable` (0056) is what `selectServingHub`
              filters on, server-side AND in the client's surcharge preview, so
              turning it on is what makes real clients start being served from
              here. Defaults OFF to match the column default: a new hub is
              invisible to booking until someone deliberately opens it. */}
          <label
            className={`flex cursor-pointer items-center justify-between gap-4 rounded-xl border p-4 transition-colors ${
              bookable
                ? "border-primary/40 bg-primary/5"
                : "border-border bg-muted/30 hover:bg-muted/50"
            }`}
          >
            <span>
              <span className="block text-sm font-semibold text-foreground">Open for bookings</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Clients within this hub&apos;s service radius can be served from here. Leave off
                until the hub has staff and a verified location.
              </span>
            </span>
            <input
              checked={bookable}
              className="size-4 rounded border-border accent-primary"
              onChange={(event) => setBookable(event.target.checked)}
              type="checkbox"
            />
          </label>

          {/* A label, not a switch: nothing operational reads `is_pilot` — no
              migration and no Edge Function. Kept because it records where the
              pilot runs, but it serves nobody a booking on its own. */}
          <label
            className={`flex cursor-pointer items-center justify-between gap-4 rounded-xl border p-4 transition-colors ${
              isPilot
                ? "border-primary/40 bg-primary/5"
                : "border-border bg-muted/30 hover:bg-muted/50"
            }`}
          >
            <span>
              <span className="block text-sm font-semibold text-foreground">Pilot hub</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Records this as the initial operating location. A label only — it does not affect
                whether bookings reach this hub.
              </span>
            </span>
            <input
              checked={isPilot}
              className="size-4 rounded border-border accent-primary"
              onChange={(event) => setIsPilot(event.target.checked)}
              type="checkbox"
            />
          </label>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button disabled={!canSubmit} type="submit">
              <PlusIcon weight="bold" />
              {createHub.isPending ? "Creating…" : "Create hub"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function HubsPage() {
  const { data: hubs, isLoading } = useHubsAdmin();
  const { data: portfolio, isLoading: portfolioLoading } = useHubPortfolioSummary();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  return (
    <div className="min-w-0 space-y-8">
      <PageHeader
        actions={
          <Button onClick={() => setIsCreateOpen(true)}>
            <PlusIcon weight="bold" />
            New hub
          </Button>
        }
        description="Review each hub's operating picture, coverage, bookings, and delivered value."
        title="Hubs"
      />

      <CreateHubDialog onOpenChange={setIsCreateOpen} open={isCreateOpen} />

      <section>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-foreground">Hub portfolio</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Operational numbers cover the last 30 days.
            </p>
          </div>
          <p className="text-xs text-muted-foreground">Select a hub to open its workspace.</p>
        </div>
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((index) => (
              <Skeleton className="h-64 motion-reduce:animate-none" key={index} />
            ))}
          </div>
        ) : (hubs?.length ?? 0) === 0 ? (
          <Empty>No hubs yet. Create the first hub above.</Empty>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {hubs?.map((hub) => (
              <HubCard
                hub={hub}
                key={hub.id}
                summary={portfolioLoading ? EMPTY_SUMMARY : (portfolio?.[hub.id] ?? EMPTY_SUMMARY)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
